import { Loading } from "../../app/Status";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { buildingArea, floorAreas, formatM2, sortFloorsByLevel, type BuildingDto } from "@klantpunt/shared";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { Tabs } from "../../app/Choice";
import { PageHeader } from "../../app/PageHeader";
import { buildingPath } from "../../app/navItems";
import { IconCheck, IconChevronDown, IconChevronUp, IconDraw, IconPencil, IconPlus, IconUpload } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { AssetDialog } from "../assets/AssetDialog";
import { usageText } from "../dossier/UsageFunctions";
import { BuildingContacts } from "../directory/ContactLinks";
import { AddRoomDialog, BuildingDialog, FloorDialog, RoomDialog } from "./dialogs";
import { ImportDialog } from "./ImportDialog";
import { ClientHistory } from "./ClientHistory";
import { MoveDialog } from "./MoveDialog";
import { IntakeSettings } from "./IntakeSettings";
import { NotificationsPanel } from "./NotificationsPanel";
import { ScreensPanel } from "./ScreensPanel";
import { AnnouncementsPanel } from "./AnnouncementsPanel";
import { buildingKey, useBuilding, useBuildingCache } from "./useBuilding";

type Tab = "gegevens" | "verdiepingen" | "contacten" | "schermen" | "notificaties";
const TABS: Tab[] = ["gegevens", "verdiepingen", "contacten", "schermen", "notificaties"];

type Dialog =
  | { type: "building" }
  | { type: "floor"; id?: string }
  | { type: "import" }
  | { type: "move" }
  | { type: "addRoom"; floorId: string }
  | { type: "room"; id: string }
  | { type: "asset"; floorId: string; roomId: string }
  | null;

/**
 * Gebouw (menu → Gebouw): gegevens, verdiepingen en ruimtes, en je eigen notificaties. Vervangt het
 * ☰-gebouwmenu. Intekenen gebeurt op de tekening; deze pagina stuurt je daarheen.
 */
export function BuildingSettings() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  const { data: b, isPending, error } = useBuilding(id);
  const cache = useBuildingCache(id);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // "meldingen" was de oude naam van Notificaties (links in eerdere mails).
  const tabParam = params.get("tab");
  const tabRaw = (tabParam === "meldingen" ? "notificaties" : tabParam) as Tab | null;
  const tab: Tab = tabRaw && TABS.includes(tabRaw) ? tabRaw : "verdiepingen";
  const setTab = (t: Tab) => setParams(t === "verdiepingen" ? {} : { tab: t }, { replace: true });
  const [floorId, setFloorId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const canManage = can(me, "building.manage");
  const seesContacts = hasModule(me, "directory", "view");
  const editsContacts = hasModule(me, "directory", "edit");
  const [addingContact, setAddingContact] = useState(false);
  const [addingScreen, setAddingScreen] = useState(false);
  const [ordering, setOrdering] = useState(false);

  if (isPending) return <Loading page />;
  if (error || !b) return <div className="center">Dit gebouw bestaat niet (meer) of je hebt er geen toegang toe.</div>;

  const floor = b.floors.find((f) => f.id === floorId) ?? b.floors[0];
  const rooms = floor ? b.rooms.filter((r) => r.floorId === floor.id) : [];
  const areas = floor ? floorAreas(floor.scale, rooms) : null;
  const total = buildingArea(b.floors, b.rooms);
  const onPlan = (q: string) => navigate(`${buildingPath(id, "tekening")}?${q}`);
  const byLevel = sortFloorsByLevel(b.floors);
  const outOfOrder = byLevel.some((f, i) => f.id !== b.floors[i]?.id);
  async function reorder(ids: string[]) {
    setOrdering(true);
    try {
      cache.set(await api<BuildingDto>(`/buildings/${id}/floors/reorder`, { method: "POST", body: { ids } }));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Volgorde opslaan mislukt");
    } finally {
      setOrdering(false);
    }
  }
  const move = (i: number, d: -1 | 1) => {
    const ids = b.floors.map((f) => f.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    void reorder(ids);
  };

  const action =
    tab === "contacten" ? (
      editsContacts && (
        <button type="button" className="btn primary" onClick={() => setAddingContact(true)}>
          <IconPlus size={20} aria-hidden="true" />
          Koppelen
        </button>
      )
    ) : !canManage || tab === "notificaties" ? null : tab === "schermen" ? (
      <button type="button" className="btn primary" onClick={() => setAddingScreen(true)}>
        <IconPlus size={20} aria-hidden="true" />
        Scherm
      </button>
    ) : tab === "gegevens" ? (
      <button type="button" className="btn primary" onClick={() => setDialog({ type: "building" })}>
        <IconPencil size={18} aria-hidden="true" />
        Gegevens bewerken
      </button>
    ) : (
      <button type="button" className="btn primary" onClick={() => setDialog({ type: "floor" })}>
        <IconPlus size={20} aria-hidden="true" />
        Verdieping
      </button>
    );

  return (
    <div className="page bsettings">
      <PageHeader
        label={
          <>
            Gebouw · <span className="code">{b.code}</span>
          </>
        }
        title={b.name}
        action={action}
        menu={
          canManage && tab === "verdiepingen" ? (
            <button type="button" className="menuitem" onClick={() => setDialog({ type: "import" })}>
              <IconUpload size={20} aria-hidden="true" />
              Tekeningen importeren
            </button>
          ) : undefined
        }
        tabs={
          <Tabs
            label="Onderdeel"
            items={[
              { value: "gegevens", label: "Gegevens" },
              { value: "verdiepingen", label: "Verdiepingen", count: b.floors.length },
              ...(seesContacts ? [{ value: "contacten" as const, label: "Contacten" }] : []),
              ...(canManage ? [{ value: "schermen" as const, label: "Schermen" }] : []),
              { value: "notificaties", label: "Notificaties" },
            ]}
            value={tab}
            onChange={setTab}
          />
        }
      />

      {tab === "gegevens" && (
        <section className="panel" aria-label="Gegevens">
          <dl className="subinfo">
            <dt>Code</dt>
            <dd className="code">{b.code}</dd>
            <dt>Naam</dt>
            <dd>{b.name}</dd>
            <dt>Adres</dt>
            <dd>{[b.address, b.city].filter(Boolean).join(", ") || <span className="muted">Geen adres</span>}</dd>
            <dt>Oppervlakte</dt>
            <dd>
              {total.measured ? (
                <>
                  {formatM2(total.total)}{" "}
                  <span className="muted small">
                    netto, som van {total.measured} ingetekende {total.measured === 1 ? "ruimte" : "ruimtes"}
                    {total.measured < total.rooms && ` (${total.rooms - total.measured} zonder oppervlakte)`}
                  </span>
                </>
              ) : (
                <span className="muted">
                  Nog niet bekend. Stel per verdieping de schaal van de tekening in en teken de ruimtes in.
                </span>
              )}
            </dd>
            <dt>Gebruiksfunctie</dt>
            <dd>
              {b.usageFunctions?.length ? (
                usageText(b.usageFunctions)
              ) : (
                <span className="muted">Nog niet ingevuld</span>
              )}
            </dd>
            <dt>Klant</dt>
            <dd>
              {b.client.isUnassigned ? <span className="muted">{b.client.name}</span> : b.client.name}
              {b.site && ` · ${b.site.name}`}
            </dd>
            {b.robawsClientId && (
              <>
                <dt>Robaws-klant</dt>
                <dd>#{b.robawsClientId}</dd>
              </>
            )}
            {b.robawsProjectId && (
              <>
                <dt>Robaws-project</dt>
                <dd>#{b.robawsProjectId}</dd>
              </>
            )}
          </dl>
          {canManage && (
            <div className="btnrow">
              <button type="button" className="btn" onClick={() => setDialog({ type: "move" })}>
                Naar andere klant
              </button>
            </div>
          )}
          {hasModule(me, "intake") && <IntakeSettings building={b} canManage={canManage} onSaved={cache.set} />}
          <ClientHistory buildingId={b.id} />
          {canManage && (
            <p className="muted small">Archiveren of definitief verwijderen staat onder Gegevens bewerken.</p>
          )}
        </section>
      )}

      {tab === "verdiepingen" && (
        <div className="bs-split">
          <section className="panel" aria-labelledby="bs-floors">
            <div className="bs-head">
              <h2 id="bs-floors">Verdiepingen</h2>
              {canManage && outOfOrder && (
                <button
                  type="button"
                  className="btn small"
                  disabled={ordering}
                  title="Kelders onderaan, dan begane grond, entresol, de verdiepingen en het dak"
                  onClick={() => void reorder(byLevel.map((f) => f.id))}
                >
                  Sorteer op niveau
                </button>
              )}
            </div>
            {total.measured > 0 && (
              <p className="muted small" data-testid="bs-total-area">
                Totaal {formatM2(total.total)} aan ingetekende ruimtes
                {total.scaledFloors < b.floors.length &&
                  ` · ${b.floors.length - total.scaledFloors} ${b.floors.length - total.scaledFloors === 1 ? "verdieping" : "verdiepingen"} zonder schaal`}
              </p>
            )}
            {b.floors.length === 0 ? (
              <p className="muted">
                Nog geen verdiepingen. {canManage && "Voeg er een toe, of importeer de tekeningen (onder ⋯)."}
              </p>
            ) : (
              <ul className="bs-list">
                {b.floors.map((f, i) => {
                  const fr = b.rooms.filter((r) => r.floorId === f.id);
                  const n = fr.length;
                  const fa = floorAreas(f.scale, fr);
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        className="bs-row"
                        aria-current={f.id === floor?.id}
                        onClick={() => setFloorId(f.id)}
                      >
                        <span className="lvl">{f.level}</span>
                        <span className="grow">
                          <strong>{f.name}</strong>
                          <span className="muted small block">
                            {n} {n === 1 ? "ruimte" : "ruimtes"} · {f.plan ? "tekening" : "geen tekening"}
                            {fa.measured > 0 && ` · ${formatM2(fa.total)}`}
                          </span>
                        </span>
                      </button>
                      {canManage && b.floors.length > 1 && (
                        <span className="bs-move">
                          <button
                            type="button"
                            className="btn ghost ico small"
                            aria-label={`${f.name} omhoog`}
                            title="Omhoog"
                            disabled={ordering || i === 0}
                            onClick={() => move(i, -1)}
                          >
                            <IconChevronUp size={18} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn ghost ico small"
                            aria-label={`${f.name} omlaag`}
                            title="Omlaag"
                            disabled={ordering || i === b.floors.length - 1}
                            onClick={() => move(i, 1)}
                          >
                            <IconChevronDown size={18} aria-hidden="true" />
                          </button>
                        </span>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className="btn ghost ico small"
                          aria-label={`${f.name} bewerken`}
                          title="Verdieping bewerken of verwijderen"
                          onClick={() => setDialog({ type: "floor", id: f.id })}
                        >
                          <IconPencil size={18} aria-hidden="true" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {floor && (
            <section className="panel" aria-labelledby="bs-rooms">
              <div className="bs-head">
                <h2 id="bs-rooms">Ruimtes · {floor.name}</h2>
                <span className="btnrow">
                  {canManage && floor.plan && (
                    <button type="button" className="btn small" onClick={() => onPlan(`schaal=${floor.id}`)}>
                      {floor.scale ? "Schaal" : "Schaal instellen"}
                    </button>
                  )}
                  {canManage && floor.plan && rooms.length > 0 && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => onPlan(`teken=alle&verdieping=${floor.id}`)}
                    >
                      <IconDraw size={18} aria-hidden="true" />
                      Intekenen
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => setDialog({ type: "addRoom", floorId: floor.id })}
                    >
                      <IconPlus size={18} aria-hidden="true" />
                      Ruimte
                    </button>
                  )}
                </span>
              </div>
              {floor.plan && areas && (
                <p className="muted small" data-testid="bs-floor-area">
                  {!areas.scaled
                    ? "Nog geen schaal: stel die in met een bekende maat om de m² van de ruimtes te zien."
                    : areas.measured
                      ? `${formatM2(areas.total)} aan ingetekende ruimtes${areas.measured < rooms.length ? ` (${rooms.length - areas.measured} nog niet ingetekend)` : ""}`
                      : "Schaal ingesteld. Teken de ruimtes in om hun oppervlakte te zien."}
                </p>
              )}
              {rooms.length === 0 ? (
                <p className="muted">
                  Nog geen ruimtes op deze verdieping.
                  {canManage &&
                    (floor.plan
                      ? " Voeg ze toe met + Ruimte, of teken ze direct op de tekening."
                      : " Voeg ze toe met + Ruimte.")}
                </p>
              ) : (
                <ul className="bs-list">
                  {rooms.map((r) => {
                    const assets = b.assets.filter((x) => x.roomId === r.id).length;
                    const m2 = areas?.byRoom.get(r.id);
                    return (
                      <li key={r.id}>
                        <span className="bs-row static">
                          <span className="grow">
                            {r.name}
                            {assets > 0 && (
                              <span className="muted small block">
                                {assets} {assets === 1 ? "asset" : "assets"}
                              </span>
                            )}
                          </span>
                          {floor.plan &&
                            (r.shape ? (
                              <span className="muted small bs-drawn" title="Ingetekend op de tekening">
                                <IconCheck size={16} aria-hidden="true" /> {m2 != null ? formatM2(m2) : "ingetekend"}
                              </span>
                            ) : (
                              canManage && (
                                <Link
                                  className="btn small ghost"
                                  to={`${buildingPath(id, "tekening")}?teken=${r.id}`}
                                  aria-label={`${r.name} intekenen`}
                                >
                                  intekenen
                                </Link>
                              )
                            ))}
                        </span>
                        {canManage && (
                          <button
                            type="button"
                            className="btn ghost ico small"
                            aria-label={`${r.name} bewerken`}
                            title="Ruimte bewerken of verwijderen"
                            onClick={() => setDialog({ type: "room", id: r.id })}
                          >
                            <IconPencil size={18} aria-hidden="true" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </div>
      )}

      {tab === "contacten" && seesContacts && (
        <BuildingContacts
          building={b}
          canEdit={editsContacts}
          canManageClients={me?.role === "owner" || me?.role === "beheerder"}
          adding={addingContact}
          onAddDone={() => setAddingContact(false)}
        />
      )}

      {tab === "schermen" && canManage && (
        <>
          <ScreensPanel building={b} creating={addingScreen} onCreateDone={() => setAddingScreen(false)} />
          <AnnouncementsPanel building={b} />
        </>
      )}

      {tab === "notificaties" && <NotificationsPanel building={b} />}

      {dialog?.type === "building" && (
        <BuildingDialog
          building={b}
          onClose={() => setDialog(null)}
          onSaved={(nb) => {
            cache.set(nb);
            setDialog(null);
            toast("Gebouw bijgewerkt");
          }}
          onRemoved={() => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: ["buildings"] });
            qc.removeQueries({ queryKey: buildingKey(b.id) });
            navigate("/", { replace: true });
          }}
        />
      )}
      {dialog?.type === "floor" && (
        <FloorDialog
          building={b}
          floorId={dialog.id}
          onClose={() => setDialog(null)}
          onSaved={(nb, newId) => {
            cache.set(nb);
            setDialog(null);
            if (newId) setFloorId(newId);
          }}
        />
      )}
      {dialog?.type === "move" && <MoveDialog buildings={[b]} onClose={() => setDialog(null)} />}
      {dialog?.type === "import" && (
        <ImportDialog
          building={b}
          initialFiles={[]}
          onClose={() => setDialog(null)}
          onDone={(nb, first) => {
            cache.set(nb);
            setDialog(null);
            if (first) setFloorId(first);
          }}
          onPartial={(nb) => cache.set(nb)}
        />
      )}
      {dialog?.type === "addRoom" && (
        <AddRoomDialog
          floorId={dialog.floorId}
          hasPlan={Boolean(b.floors.find((f) => f.id === dialog.floorId)?.plan)}
          onClose={() => setDialog(null)}
          onSaved={(r, drawNow) => {
            cache.upsertRoom(r);
            setDialog(null);
            if (drawNow) onPlan(`teken=${r.id}`);
          }}
        />
      )}
      {dialog?.type === "room" &&
        (() => {
          const room = b.rooms.find((r) => r.id === dialog.id);
          if (!room) return null;
          return (
            <RoomDialog
              building={b}
              room={room}
              canDraw={Boolean(b.floors.find((f) => f.id === room.floorId)?.plan)}
              onClose={() => setDialog(null)}
              onDraw={() => onPlan(`teken=${room.id}`)}
              onEditVertices={() => onPlan(`hoeken=${room.id}`)}
              onAddInstallation={() => setDialog({ type: "asset", floorId: room.floorId, roomId: room.id })}
              onEditInstallation={(aid) => navigate(`${buildingPath(id, "assets")}?asset=${aid}`)}
            />
          );
        })()}
      {dialog?.type === "asset" && (
        <AssetDialog
          building={b}
          initial={{ floorId: dialog.floorId, roomId: dialog.roomId }}
          onClose={() => setDialog(null)}
          onSaved={(a) => navigate(`${buildingPath(id, "assets")}?asset=${a.id}`)}
        />
      )}
    </div>
  );
}
